import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsSales, type GoodsSaleListItem } from '@/api/goodsSales';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

export default function GoodsSalesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['goods-sales'],
    queryFn: () => goodsSales.list({ limit: 200 }),
  });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });

  const contractorName = useMemo(() => {
    const map = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [contractorsQ.data]);

  const cols = useMemo<ColumnDef<GoodsSaleListItem>[]>(() => [
    { key: 'document_number', header: t('gs.number'), width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'document_date', header: t('gs.date'), width: 170, sortValue: (r) => r.document_date ?? '', cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString(i18n.language) : '' },
    { key: 'contractor_id', header: t('gs.contractor'), sortValue: (r) => contractorName.get(r.contractor_id) ?? '', cell: (r) => contractorName.get(r.contractor_id) ?? '' },
    { key: 'total_amount', header: t('gs.total'), width: 140, align: 'right', sortValue: (r) => Number(r.total_amount), cell: (r) => Number(r.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    { key: 'posted', header: t('gs.posted'), width: 130, align: 'center', sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no') },
  ], [t, i18n.language, contractorName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('gs.title')}</h2>
        <button className="primary" onClick={() => navigate('/goods-sales/new')}>
          + {t('gs.new')}
        </button>
      </div>
      <DataTable<GoodsSaleListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/goods-sales/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
